import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import AppShell from "@/components/AppShell";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import AcceptInvite from "@/pages/AcceptInvite";
import Dashboard from "@/pages/landlord/Dashboard";
import Properties from "@/pages/landlord/Properties";
import Tenants from "@/pages/landlord/Tenants";
import Payments from "@/pages/landlord/Payments";
import Maintenance from "@/pages/landlord/Maintenance";
import Messages from "@/pages/landlord/Messages";
import Inbox from "@/pages/landlord/Inbox";
import Billing from "@/pages/landlord/Billing";
import Settings from "@/pages/landlord/Settings";
import TenantDashboard from "@/pages/tenant/TenantDashboard";
import TenantPayments from "@/pages/tenant/TenantPayments";
import TenantMaintenance from "@/pages/tenant/TenantMaintenance";
import TenantDocuments from "@/pages/tenant/TenantDocuments";
import TenantMessages from "@/pages/tenant/TenantMessages";
import TenantMoveIn from "@/pages/tenant/TenantMoveIn";
import TenantMoveOut from "@/pages/tenant/TenantMoveOut";
import MoveOutInspection from "@/pages/landlord/MoveOutInspection";
import Listing from "@/pages/Listing";
import PaymentReturn from "@/pages/PaymentReturn";
import BillingReturn from "@/pages/BillingReturn";
import {
  LayoutDashboard, Building2, Users, Wallet, Wrench, MessageSquareText, Settings as SettingsIcon,
  Home as HomeIcon, CreditCard, FileText, Inbox as InboxIcon, ClipboardCheck, ClipboardList,
} from "lucide-react";

const LANDLORD_NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/properties", label: "Properties", icon: Building2 },
  { to: "/tenants", label: "Tenants", icon: Users },
  { to: "/payments", label: "Rent & Section 8", icon: Wallet },
  { to: "/maintenance", label: "Maintenance", icon: Wrench },
  { to: "/messages", label: "Notices", icon: MessageSquareText },
  { to: "/inbox", label: "Inbox", icon: InboxIcon, badge: true },
  { to: "/billing", label: "Billing", icon: CreditCard },
  { to: "/settings", label: "Settings", icon: SettingsIcon },
];

const TENANT_NAV = [
  { to: "/portal", label: "My Home", icon: HomeIcon },
  { to: "/portal/payments", label: "Pay Rent", icon: CreditCard },
  { to: "/portal/messages", label: "Messages", icon: MessageSquareText, badge: true },
  { to: "/portal/documents", label: "Documents", icon: FileText },
  { to: "/portal/maintenance", label: "Maintenance", icon: Wrench },
  { to: "/portal/move-in", label: "Move-in Checklist", icon: ClipboardCheck },
  { to: "/portal/move-out", label: "Move-out", icon: ClipboardList },
];

function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={user.role === "landlord" ? "/dashboard" : "/portal"} replace />;
}

function AppRouter() {
  const location = useLocation();
  // Google OAuth returns to {origin}/dashboard#session_id=… — exchange it before any protected route runs.
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<RootRedirect />} />
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/join" element={<AcceptInvite />} />
      <Route path="/listing/:token" element={<Listing />} />
      <Route path="/payment/success" element={<PaymentReturn />} />
      <Route path="/payment/cancel" element={<PaymentReturn />} />
      <Route path="/billing/return" element={<BillingReturn />} />

      <Route element={<ProtectedRoute role="landlord" />}>
        <Route element={<AppShell nav={LANDLORD_NAV} brandSub="Landlord Studio" />}>
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/properties" element={<Properties />} />
          <Route path="/tenants" element={<Tenants />} />
          <Route path="/tenants/:tid/move-out" element={<MoveOutInspection />} />
          <Route path="/payments" element={<Payments />} />
          <Route path="/maintenance" element={<Maintenance />} />
          <Route path="/messages" element={<Messages />} />
          <Route path="/inbox" element={<Inbox />} />
          <Route path="/billing" element={<Billing />} />
          <Route path="/settings" element={<Settings />} />
        </Route>
      </Route>

      <Route element={<ProtectedRoute role="tenant" />}>
        <Route element={<AppShell nav={TENANT_NAV} brandSub="Tenant Portal" />}>
          <Route path="/portal" element={<TenantDashboard />} />
          <Route path="/portal/payments" element={<TenantPayments />} />
          <Route path="/portal/messages" element={<TenantMessages />} />
          <Route path="/portal/documents" element={<TenantDocuments />} />
          <Route path="/portal/maintenance" element={<TenantMaintenance />} />
          <Route path="/portal/move-in" element={<TenantMoveIn />} />
          <Route path="/portal/move-out" element={<TenantMoveOut />} />
        </Route>
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
